import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import ChildSelector from "@/pages/veli/components/ChildSelector";
import { useVeliChild } from "@/hooks/useVeli";
import {
  useStudentAttendance,
  ATTENDANCE_STATUS_LABELS,
  ATTENDANCE_STATUS_COLORS,
} from "@/hooks/useAttendance";

export default function Devamsizlik() {
  const { children, activeChild, activeUserId, setActiveUserId, loading: childrenLoading } =
    useVeliChild();
  const { data, loading, error, reload } = useStudentAttendance(activeChild?.userId ?? null);

  if (childrenLoading) return <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />;

  if (children.length === 0) {
    return (
      <div className="space-y-6">
        <SectionHeader
          title="Devamsızlık"
          description="Öğrencinin ders katılım ve devamsızlık durumunu gör."
        />
        <EmptyState
          icon="ri-user-unfollow-line"
          title="Henüz öğrenci hesabı bağlanmadı"
          description="Çocuğunuzun hesabını eşleştirdiğinizde devamsızlık durumu burada görünecek."
        />
        <PanelLinks
          links={[{ label: "Çocuğunu Eşleştir", href: "/veli/profil", icon: "ri-link" }]}
        />
      </div>
    );
  }

  const list = data ?? [];
  const present = list.filter((r) => r.status === "present").length;
  const absent = list.filter((r) => r.status === "absent").length;
  const late = list.filter((r) => r.status === "late").length;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Devamsızlık"
        description={`${activeChild?.fullName ?? "Öğrencinin"} ders katılım ve devamsızlık durumu.`}
      />
      <ChildSelector
        children={children}
        activeUserId={activeUserId}
        onSelect={setActiveUserId}
      />

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-user-unfollow-line"
          title="Henüz yoklama kaydı yok"
          description="Öğretmen yoklama aldığında çocuğunuzun katılım durumu burada görünecek."
        />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-xs font-medium text-foreground-500">Katıldı</p>
              <p className="mt-2 font-heading font-bold text-2xl text-accent-700">{present}</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-xs font-medium text-foreground-500">Katılmadı</p>
              <p className="mt-2 font-heading font-bold text-2xl text-red-700">{absent}</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-xs font-medium text-foreground-500">Geç Kaldı</p>
              <p className="mt-2 font-heading font-bold text-2xl text-foreground-500">{late}</p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/60">
                    <th className="px-4 py-3 text-left font-semibold text-foreground-700">Ders</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-700">Tarih</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-700">Katılım</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r) => (
                    <tr key={r.id} className="border-b border-background-200 hover:bg-background-100/40 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground-950">{r.classTitle}</p>
                        <p className="text-xs text-foreground-500">{r.subject}</p>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{r.date}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${ATTENDANCE_STATUS_COLORS[r.status] ?? "bg-background-100 text-foreground-600"}`}
                        >
                          {r.status === "present" && <i className="ri-check-line" aria-hidden="true"></i>}
                          {r.status === "absent" && <i className="ri-close-line" aria-hidden="true"></i>}
                          {r.status === "late" && <i className="ri-time-line" aria-hidden="true"></i>}
                          {ATTENDANCE_STATUS_LABELS[r.status] ?? r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <PanelLinks
        links={[
          { label: "Dersler", href: "/veli/dersler", icon: "ri-book-2-line" },
          { label: "Haftalık Rapor", href: "/veli/haftalik-rapor", icon: "ri-bar-chart-line" },
        ]}
      />
    </div>
  );
}